<script setup lang="ts">
import { Dialog, DialogContent, IconButton } from '@laam/ui/base';
import { PhCaretLeft, PhCaretRight } from '@phosphor-icons/vue';
import CarouselSlide from './media-carousel/CarouselSlide.vue';
import CarouselThumbnail from './media-carousel/CarouselThumbnail.vue';
import { MediaType } from './media-carousel/types';

interface ImageViewerProps {
	media: {
		type: MediaType;
		src: string;
		poster?: string;
	}[];
	alt: string;
	index: number;
	fallbackPoster: string;
}

const props = defineProps<ImageViewerProps>();
const isOpen = defineModel<boolean>({ default: false, required: true });

const imgIndex = ref(0);
const sidebarImageRef = ref<HTMLDivElement[] | null>(null);

watch(
	() => props.index,
	(val) => {
		imgIndex.value = val;
	},
);

function scrollIntoView(id: number) {
	const element = sidebarImageRef.value ? sidebarImageRef.value[id] : null;
	element?.scrollIntoView({
		behavior: 'smooth',
		block: 'center',
		inline: 'nearest',
	});
}

function onPrev() {
	if (imgIndex.value === null) return;
	if (imgIndex.value > 0) {
		imgIndex.value--;
	} else {
		imgIndex.value = props.media.length - 1;
	}

	scrollIntoView(imgIndex.value);
}

function onNext() {
	if (imgIndex.value === null) return;
	if (imgIndex.value < props.media.length - 1) {
		imgIndex.value++;
	} else {
		imgIndex.value = 0;
	}
	scrollIntoView(imgIndex.value);
}

function onClickThumb(index: number) {
	imgIndex.value = index;
}

onMounted(() => {
	imgIndex.value = props.index;
});
</script>

<template>
	<Dialog v-model:open="isOpen">
		<DialogContent
			:show-icon-with-bg="true"
			class="bg-transparent border-none rounded-none p-none"
		>
			<div class="relative grid w-[90vw] h-[95vh] grid-cols-10">
				<!-- Sidebar rows -->
				<div
					ref="gallery"
					class="overflow-auto rounded images-sidebar shadow-sidebar"
				>
					<div class="flex flex-col gap-md justify-center">
						<div
							v-for="(content, i) in media"
							ref="sidebarImageRef"
							:key="i"
							class="object-contain cursor-pointer gap-lg"
							@click.stop="onClickThumb(i)"
						>
							<CarouselThumbnail
								:type="content.type"
								:src="
									content.type === MediaType.IMAGE
										? content.src
										: content.poster ?? fallbackPoster
								"
								:alt="alt"
								:is-active="i === imgIndex"
							/>
						</div>
					</div>
				</div>

				<!-- The Content -->
				<div class="relative flex justify-center col-span-9 col-start-2">
					<div class="flex items-center justify-center gap-7xl">
						<IconButton
							class="absolute bottom-1/2 left-[3rem] hover:cursor-pointer"
							variant="secondary"
							size="xl"
							rounded="full"
							@click.stop="onPrev"
						>
							<PhCaretLeft size="20" class="" />
						</IconButton>

						<div
							class="flex items-center justify-center w-full h-full min-w-max"
						>
							<div
								v-for="(content, loopIndex) in media"
								:key="loopIndex"
								class="h-2/3"
							>
								<div
									v-show="loopIndex === imgIndex"
									class="h-full"
									:class="{
										'pdp__image-slider-image image':
											content.type === MediaType.IMAGE,
									}"
								>
									<CarouselSlide
										:type="content.type"
										:src="content.src"
										:alt="props.alt"
										:is-active="loopIndex === imgIndex"
										:poster="content.poster ?? fallbackPoster"
									/>
								</div>
							</div>
						</div>

						<IconButton
							class="absolute bottom-1/2 right-[3rem] hover:cursor-pointer"
							variant="secondary"
							size="xl"
							rounded="full"
							@click.stop="onNext"
						>
							<PhCaretRight size="20" />
						</IconButton>
					</div>
				</div>
			</div>
		</DialogContent>
	</Dialog>
</template>

<style scoped>
.viewer-modal-inner {
	display: flex;
	align-self: center;
	/* position: absolute; */
	margin: 0% auto; /* 15% from the top and centered */
	padding: 40px 24px;
	/* width: fit-content; *ould be more or less, depending on screen size */
	height: 100vh;
	flex-direction: row;
}

.zoom-in {
	transform: scale(1.5);
}

.zoom-out {
	transform: scale(1);
}

.vgs__gallery__container::-webkit-scrollbar {
	display: none; /* for Chrome, Safari, and Opera */
}

.images-sidebar::-webkit-scrollbar {
	display: none; /* for Chrome, Safari, and Opera */
}

.pdp__image-slider-image:hover {
	transform: scale(1.5);
}
.pdp__image-slider-image {
	transition: transform 0.3s ease-out;
}
.modal-enter-active {
	transition: all 300ms ease-out;
}
.modal-leave-active {
	transition: all 300ms ease-in;
}

.modal-leave-active {
	transition-delay: 0.25s;
}
.modal-enter,
.modal-leave-to {
	opacity: 0;
}
.modal-enter-active .viewer-modal-inner {
	transition: all 300ms ease-out;
}
.modal-leave-active .viewer-modal-inner {
	transition: all 300ms ease-in;
}
.modal-enter-active .viewer-modal-inner {
	transition-delay: 0.3s;
}
.modal-enter .viewer-modal-inner,
.modal-leave-to .viewer-modal-inner {
	/* transform: translateX(+100%); */
	opacity: 0.001;
}
</style>
